import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, Clock, Download, FileCheck2, Loader2,
  Paperclip, ShieldCheck,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  attachManualEvidence, controlStatuses, exportAuditorPacket,
  listControlEvidence, listControls, updateControl,
} from "@/lib/compliance.functions";

export const Route = createFileRoute("/_authenticated/app/settings/compliance")({
  head: () => ({
    meta: [
      { title: "Compliance evidence — CairnOS" },
      {
        name: "description",
        content:
          "SOC 2-style control register, evidence vault, and one-click auditor packet export.",
      },
    ],
  }),
  component: ComplianceVault,
});

const KINDS = ["document", "link", "screenshot", "attestation", "policy", "note"] as const;

function statusBadge(s: string) {
  switch (s) {
    case "attested":
      return <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30">attested</Badge>;
    case "implemented":
      return <Badge className="bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30">implemented</Badge>;
    case "in_progress":
      return <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30">in progress</Badge>;
    default:
      return <Badge variant="outline">not started</Badge>;
  }
}

function ComplianceVault() {
  const qc = useQueryClient();
  const listFn = useServerFn(listControls);
  const exportFn = useServerFn(exportAuditorPacket);

  const [selected, setSelected] = useState<any | null>(null);
  const [familyFilter, setFamilyFilter] = useState<string>("all");

  const q = useQuery({
    queryKey: ["compliance-controls"],
    queryFn: () => listFn(),
    refetchInterval: 60_000,
  });

  const rows: any[] = q.data?.rows ?? [];
  const families = useMemo(
    () => Array.from(new Set(rows.map((r) => r.family))).sort(),
    [rows],
  );
  const filtered = familyFilter === "all" ? rows : rows.filter((r) => r.family === familyFilter);

  const total = rows.length;
  const attested = rows.filter((r) => r.status === "attested").length;
  const implemented = rows.filter((r) => r.status === "implemented").length;
  const withEvidence = rows.filter((r) => r.evidenceCount > 0).length;
  const coverage = total ? Math.round((withEvidence / total) * 100) : 0;

  const exportPacket = async () => {
    try {
      const res = await exportFn();
      const blob = new Blob([res.csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `auditor-packet-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Auditor packet exported");
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-6 w-6" /> Compliance evidence vault
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            SOC 2 control register with automated nightly snapshots and a manual evidence drawer.
            Export an auditor-ready packet at any time.
          </p>
        </div>
        <Button onClick={exportPacket} variant="outline">
          <Download className="h-4 w-4 mr-2" /> Export auditor packet
        </Button>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Controls" value={total} sub="total in scope" />
        <StatCard label="Coverage" value={`${coverage}%`} sub={`${withEvidence}/${total} with evidence`} accent />
        <StatCard label="Implemented" value={implemented} sub="status = implemented" />
        <StatCard label="Attested" value={attested} sub="auditor-signed" />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Control Register</CardTitle>
            <CardDescription>
              Click a control to view evidence and attach new artifacts.
            </CardDescription>
          </div>
          <Select value={familyFilter} onValueChange={setFamilyFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All families" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All families</SelectItem>
              {families.map((f) => (
                <SelectItem key={f} value={f}>
                  {f}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {q.isLoading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Control</TableHead>
                  <TableHead>Family</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Evidence</TableHead>
                  <TableHead>Last captured</TableHead>
                  <TableHead>Reviewed</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow
                    key={r.id}
                    className="cursor-pointer"
                    onClick={() => setSelected(r)}
                  >
                    <TableCell className="font-mono text-xs">{r.control_id}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.family}</TableCell>
                    <TableCell className="max-w-[420px] truncate" title={r.description}>
                      {r.description}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-xs">
                        {r.evidenceCount > 0 ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                        ) : (
                          <Circle className="h-3.5 w-3.5 text-muted-foreground/40" />
                        )}
                        <span>{r.evidenceCount}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.lastEvidenceAt
                        ? formatDistanceToNow(new Date(r.lastEvidenceAt), { addSuffix: true })
                        : "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.last_reviewed_at
                        ? formatDistanceToNow(new Date(r.last_reviewed_at), { addSuffix: true })
                        : <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />never</span>}
                    </TableCell>
                    <TableCell>{statusBadge(r.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ControlDrawer
        control={selected}
        onClose={() => setSelected(null)}
        onChanged={() => qc.invalidateQueries({ queryKey: ["compliance-controls"] })}
      />
    </div>
  );
}

function StatCard({ label, value, sub, accent }: { label: string; value: number | string; sub: string; accent?: boolean }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
        <div className={`text-2xl font-semibold mt-1 ${accent ? "text-primary" : ""}`}>{value}</div>
        <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>
      </CardContent>
    </Card>
  );
}

function ControlDrawer({
  control, onClose, onChanged,
}: { control: any | null; onClose: () => void; onChanged: () => void }) {
  const open = !!control;
  const qc = useQueryClient();
  const evidenceFn = useServerFn(listControlEvidence);
  const updateFn = useServerFn(updateControl);
  const attachFn = useServerFn(attachManualEvidence);
  const [attachOpen, setAttachOpen] = useState(false);

  const evQ = useQuery({
    queryKey: ["compliance-evidence", control?.control_id],
    enabled: open,
    queryFn: () => evidenceFn({ data: { controlId: control!.control_id, limit: 100 } }),
  });

  const statusMut = useMutation({
    mutationFn: (status: string) =>
      updateFn({ data: { id: control!.id, status: status as any } }),
    onSuccess: () => {
      toast.success("Status updated");
      onChanged();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const reviewMut = useMutation({
    mutationFn: () => updateFn({ data: { id: control!.id, markReviewed: true } }),
    onSuccess: () => {
      toast.success("Marked as reviewed");
      onChanged();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        {control && (
          <>
            <SheetHeader>
              <SheetTitle className="font-mono">{control.control_id}</SheetTitle>
              <SheetDescription>{control.description}</SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Select
                    value={control.status}
                    onValueChange={(v) => statusMut.mutate(v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {controlStatuses.map((s) => (
                        <SelectItem key={s} value={s} className="font-mono text-xs">
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="self-end">
                  <Button variant="outline" onClick={() => reviewMut.mutate()}>
                    <FileCheck2 className="h-4 w-4 mr-2" /> Mark reviewed
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <div className="text-sm font-medium">Evidence timeline</div>
                <Button size="sm" onClick={() => setAttachOpen(true)}>
                  <Paperclip className="h-3.5 w-3.5 mr-1.5" /> Attach evidence
                </Button>
              </div>

              <div className="border rounded-md">
                {evQ.isLoading ? (
                  <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading evidence…
                  </div>
                ) : (evQ.data?.rows ?? []).length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    No evidence captured yet. Snapshots run nightly — or attach manually above.
                  </div>
                ) : (
                  <div className="divide-y">
                    {(evQ.data!.rows as any[]).map((e) => (
                      <div key={e.id} className="p-3 text-xs space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] font-mono">{e.kind}</Badge>
                          <span className="text-muted-foreground">
                            {formatDistanceToNow(new Date(e.captured_at), { addSuffix: true })}
                          </span>
                        </div>
                        <div className="font-mono break-all">{e.ref}</div>
                        {e.detail?.note && (
                          <div className="text-muted-foreground italic">{e.detail.note}</div>
                        )}
                        <div className="text-[10px] text-muted-foreground font-mono">
                          sha256: {(e.hash ?? "").slice(0, 16)}…
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <AttachDialog
              open={attachOpen}
              controlId={control.control_id}
              onClose={() => setAttachOpen(false)}
              onSaved={() => {
                setAttachOpen(false);
                void qc.invalidateQueries({ queryKey: ["compliance-evidence", control.control_id] });
                onChanged();
              }}
              attachFn={attachFn}
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function AttachDialog({
  open, controlId, onClose, onSaved, attachFn,
}: {
  open: boolean;
  controlId: string;
  onClose: () => void;
  onSaved: () => void;
  attachFn: ReturnType<typeof useServerFn<typeof attachManualEvidence>>;
}) {
  const [kind, setKind] = useState<(typeof KINDS)[number]>("link");
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");

  const mut = useMutation({
    mutationFn: () => attachFn({ data: { controlId, kind, ref, note } }),
    onSuccess: () => {
      toast.success("Evidence attached");
      setRef("");
      setNote("");
      onSaved();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attach evidence to {controlId}</DialogTitle>
          <DialogDescription>
            Capture a URL, document reference, or attestation. Stored with a SHA-256 hash for
            auditor traceability.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Kind</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {KINDS.map((k) => (
                  <SelectItem key={k} value={k} className="font-mono text-xs">{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="ev-ref">Reference</Label>
            <Input
              id="ev-ref"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder="https://drive.google.com/… or s3://bucket/object"
              className="font-mono text-xs"
            />
          </div>
          <div>
            <Label htmlFor="ev-note">Note</Label>
            <Textarea
              id="ev-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Optional context for the auditor"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!ref.trim() || mut.isPending}
            onClick={() => mut.mutate()}
          >
            {mut.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Attach
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}